import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { History, RotateCcw, GitCompare, Edit3, Check, X } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const Container = styled.div`
  width: 380px;
  min-width: 380px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CompareBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { background: rgba(99, 102, 241, 0.25); }
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const VersionCard = styled.div`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: default;
  transition: all 0.15s;
  margin-bottom: 2px;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const VersionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

const VersionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const VersionName = styled.div`
  font-size: 11px;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 6px;
  padding: 2px 8px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VersionTime = styled.div`
  font-size: 11px;
  color: #555;
`;

const VersionNote = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 4px;
`;

const VersionMeta = styled.div`
  font-size: 11px;
  color: #555;
  margin-top: 2px;
`;

const RenameRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
  align-items: center;
`;

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: #ddd;
  font-size: 12px;
  padding: 6px 8px;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.04);
  color: #aaa;
  cursor: pointer;
  &:hover { color: #fff; border-color: rgba(255, 255, 255, 0.3); }
`;

const RevertBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 8px;
  &:hover { background: rgba(245, 158, 11, 0.25); }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  font-size: 13px;
  padding: 24px;
  text-align: center;
`;

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('fi-FI') + ' ' + d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

const VersionHistory = ({ versions, onRevert, reverting, onOpenComparison, promptKey, onRefreshVersions, currentTemplate }) => {
  const allVersions = useMemo(() => versions || [], [versions]);
  const [editingVersion, setEditingVersion] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const startEdit = (v) => {
    setEditingVersion(v.version);
    setEditingName(v.version_name || '');
  };

  const cancelEdit = () => {
    setEditingVersion(null);
    setEditingName('');
  };

  const saveName = async (version) => {
    if (!promptKey || savingName) return;
    setSavingName(true);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_VERSION_UPDATE(promptKey, version), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ versionName: editingName }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      cancelEdit();
      if (onRefreshVersions) await onRefreshVersions();
    } catch (err) {
      console.error('Failed to rename version:', err);
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderTitle><History size={16} /> Versiohistoria</HeaderTitle>
        {(allVersions.length > 0 || currentTemplate) && (
          <CompareBtn onClick={onOpenComparison}>
            <GitCompare size={13} /> Vertaa
          </CompareBtn>
        )}
      </Header>

      <ListContainer>
        {currentTemplate && (
          <VersionCard>
            <VersionHeader>
              <VersionTitleRow>
                <VersionLabel>Nykyinen (v{currentTemplate.version})</VersionLabel>
                {currentTemplate.version_name && (
                  <VersionName title={currentTemplate.version_name}>{currentTemplate.version_name}</VersionName>
                )}
              </VersionTitleRow>
            </VersionHeader>
            {currentTemplate.change_note && <VersionNote>{currentTemplate.change_note}</VersionNote>}
            <VersionMeta>Live — tallenna uudella nimellä nimetäksesi seuraavan version</VersionMeta>
          </VersionCard>
        )}
        {allVersions.length === 0 && !currentTemplate ? (
          <EmptyState>Ei aiempia versioita</EmptyState>
        ) : allVersions.length === 0 ? (
          <EmptyState>Ei aiempia versioita historiassa</EmptyState>
        ) : (
          allVersions.map(v => (
            <VersionCard key={v.version}>
              <VersionHeader>
                <VersionTitleRow>
                  <VersionLabel>v{v.version}</VersionLabel>
                  {v.version_name && <VersionName title={v.version_name}>{v.version_name}</VersionName>}
                </VersionTitleRow>
                <VersionTime>{formatDate(v.created_at)}</VersionTime>
              </VersionHeader>
              {v.change_note && <VersionNote>{v.change_note}</VersionNote>}
              <VersionMeta>
                {v.model && <>Malli: {v.model}</>}
                {v.created_by && <> &middot; {v.created_by}</>}
              </VersionMeta>
              {editingVersion === v.version ? (
                <RenameRow>
                  <RenameInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    placeholder="Nimi versiolle, esim. 'Vakaa SWOT-formaatti'"
                  />
                  <IconBtn onClick={() => saveName(v.version)} disabled={savingName} title="Tallenna nimi">
                    <Check size={14} />
                  </IconBtn>
                  <IconBtn onClick={cancelEdit} disabled={savingName} title="Peruuta">
                    <X size={14} />
                  </IconBtn>
                </RenameRow>
              ) : (
                <RenameRow>
                  <IconBtn onClick={() => startEdit(v)} title="Nimeä versio">
                    <Edit3 size={14} />
                  </IconBtn>
                </RenameRow>
              )}
              <RevertBtn
                onClick={() => onRevert(v.version)}
                disabled={reverting}
              >
                <RotateCcw size={12} />
                {reverting ? 'Palautetaan...' : 'Palauta tämä versio'}
              </RevertBtn>
            </VersionCard>
          ))
        )}
      </ListContainer>
    </Container>
  );
};

export default VersionHistory;
